import React, { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import { StarterKit } from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Link } from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Superscript } from "@tiptap/extension-superscript";
import { Subscript } from "@tiptap/extension-subscript";
import FormulaExtension from "./extensions/formulaExtension";
import GreyBackgroundExtension, {
  BackgroundStyleAttributesExtension,
} from "./extensions/greyBackgroundExtension";
import ResizableImageExtension, {
  DEFAULT_RESIZABLE_IMAGE_WIDTH,
} from "./extensions/resizableImageExtension";

const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

function normalizeImageFiles(fileList) {
  const files = Array.from(fileList || []);
  return files.filter((file) => {
    if (!file || typeof file.type !== "string") return false;
    return file.type.startsWith("image/");
  });
}

function isExistingEditorImageDrop(dataTransfer) {
  const html = dataTransfer?.getData?.("text/html") || "";
  return (
    html.includes("lh6-image-align-") ||
    html.includes("data-lh-image-layout") ||
    html.includes("data-type=\"resizableImage\"")
  );
}

function readImageAsDataUrl(file, onReady) {
  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === "string") {
      onReady(reader.result);
    }
  };
  reader.readAsDataURL(file);
}

async function resolveImageSource(file, onImageFileUpload) {
  if (typeof onImageFileUpload === "function") {
    return onImageFileUpload(file);
  }

  return new Promise((resolve) => {
    readImageAsDataUrl(file, resolve);
  });
}

function insertImageNode(view, src, position, alt = "") {
  const nodeType = view.state.schema.nodes.resizableImage;
  if (!nodeType || !src) return false;

  try {
    const node = nodeType.create({ src, alt, width: DEFAULT_RESIZABLE_IMAGE_WIDTH });
    let tr = view.state.tr;

    if (Number.isInteger(position)) {
      const safePos = Math.max(0, Math.min(position, view.state.doc.content.size));
      tr = tr.insert(safePos, node);
    } else {
      tr = tr.replaceSelectionWith(node, false);
    }

    view.dispatch(tr.scrollIntoView());
    return true;
  } catch {
    return false;
  }
}

const FontSize = Extension.create({
  name: "fontSize",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: `${fontSize}px` }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

export default function TiptapEditor({
  htmlMode,
  onEditorReady,
  onUpdate,
  onSelectionUpdate,
  onImageFileUpload,
}) {
  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        class: "lh6-editorArea learnhut-content lh-lessonContent",
        spellcheck: "true",
        "aria-label": "Text editor",
      },
      handleKeyDown: (view, event) => {
        if (event.key !== "Tab") return false;

        event.preventDefault();
        const tr = view.state.tr.insertText("\t");
        view.dispatch(tr);
        return true;
      },
      handlePaste: (view, event) => {
        const imageFiles = normalizeImageFiles(event.clipboardData?.files);
        if (imageFiles.length === 0) return false;

        event.preventDefault();

        imageFiles.forEach((file) => {
          if (file.size > MAX_IMAGE_BYTES) return;
          resolveImageSource(file, onImageFileUpload)
            .then((src) => {
              insertImageNode(view, src, null, file.name || "");
            })
            .catch(() => {});
        });

        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        if (isExistingEditorImageDrop(event.dataTransfer)) return false;

        const imageFiles = normalizeImageFiles(event.dataTransfer?.files);
        if (imageFiles.length === 0) return false;

        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        const dropPos = coords?.pos;

        imageFiles.forEach((file) => {
          if (file.size > MAX_IMAGE_BYTES) return;
          resolveImageSource(file, onImageFileUpload)
            .then((src) => {
              insertImageNode(view, src, dropPos, file.name || "");
            })
            .catch(() => {});
        });

        return true;
      },
    },
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
      }),
      TextStyle,
      FontSize,
      Color,
      BackgroundStyleAttributesExtension,
      Link.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: true,
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: { class: "lh6-table" },
      }),
      TableRow,
      TableHeader,
      TableCell,
      Superscript,
      Subscript,
      FormulaExtension,
      GreyBackgroundExtension,
      ResizableImageExtension,
    ],
    content: "",
    onCreate: ({ editor: instance }) => {
      onEditorReady?.(instance);
    },
    onUpdate: ({ editor: instance }) => {
      onUpdate?.(instance);
    },
    onSelectionUpdate: ({ editor: instance }) => {
      onSelectionUpdate?.(instance);
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!htmlMode);
  }, [editor, htmlMode]);

  useEffect(() => {
    return () => {
      onEditorReady?.(null);
    };
  }, [onEditorReady]);

  return (
    <div className={`lh6-visualPane ${htmlMode ? "is-hidden" : "is-visible"}`}>
      <EditorContent editor={editor} />
    </div>
  );
}
